import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Globe, MapPin, Phone, Store } from 'lucide-react'
import { cn } from '@/lib/cn'
import { brandGradient } from '@/lib/color'
import { Badge, EmptyState, Spinner } from '@/components/ui'
import { CompanyAvatar } from '@/components/CompanyAvatar'
import { OfferCard } from '@/components/offers/OfferCard'
import { OfferDetail } from '@/components/offers/OfferDetail'
import { useCompanies, useMyRedemptions, useOffers } from '@/hooks/useOffers'
import { useCurrentBooklet } from '@/hooks/useCurrentBooklet'
import { CATEGORY_LABELS, type Offer } from '@shared/model'

export function CompanyPage() {
  const { companyId } = useParams<{ companyId: string }>()
  const { booklet, owned, loading: bookletLoading } = useCurrentBooklet()
  const { data: companies, loading: companiesLoading } = useCompanies()
  const { data: offers, loading: offersLoading } = useOffers(booklet?.id ?? null)
  const { usedByOffer } = useMyRedemptions(booklet?.id ?? null)

  const [openOffer, setOpenOffer] = useState<Offer | null>(null)

  const company = useMemo(
    () => companies.find((item) => item.id === companyId) ?? null,
    [companies, companyId],
  )

  const companyOffers = useMemo(
    () => offers.filter((offer) => offer.companyId === companyId),
    [offers, companyId],
  )

  if (bookletLoading || companiesLoading || offersLoading) return <Spinner />

  if (!company) {
    return (
      <EmptyState
        icon={<Store size={44} aria-hidden />}
        title="Företaget hittades inte"
        description="Det kan ha tagits bort ur häftet."
      />
    )
  }

  const remainingFor = (offer: Offer) => owned - (usedByOffer[offer.id] ?? 0)

  return (
    <div className="space-y-5">
      <Link
        to="/foretag"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft size={16} aria-hidden />
        Alla företag
      </Link>

      {/*
       * Omslaget gör företaget till en plats i stället för en rad.
       *
       * Finns ingen bild men en profilfärg används den som fond – ett
       * företag som lämnat sin färg ska inte se lika anonymt ut som ett som
       * inte lämnat något alls. Loggan ligger ovanpå och bryter kanten, så
       * att den syns mot både bild och färg.
       */}
      {(company.coverImageUrl || company.brandColor) && (
        <div
          className="relative -mt-1 h-36 overflow-hidden rounded-card sm:h-44"
          style={
            company.coverImageUrl
              ? {
                  backgroundImage: `url(${company.coverImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundImage: brandGradient(company.brandColor as string) }
          }
        >
          {/* Raster bara över foton. En profilfärg är redan vald för att
              fungera, en fotobakgrund är vad någon råkat ladda upp. */}
          {company.coverImageUrl && (
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
          )}
        </div>
      )}

      <header
        className={cn(
          'flex items-start gap-4',
          (company.coverImageUrl || company.brandColor) && '-mt-12',
        )}
      >
        <div className="rounded-2xl bg-surface p-1 shadow-card">
          <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size={76} />
        </div>
        <div className="min-w-0 flex-1 pt-12">
          <h1 className="font-display text-2xl font-extrabold text-balance">{company.name}</h1>
          <Badge className="mt-1.5">{CATEGORY_LABELS[company.category]}</Badge>
        </div>
      </header>

      {company.description && <p className="text-ink">{company.description}</p>}

      {/* Kontaktuppgifter som klickbara länkar – man står oftast i mobilen. */}
      {(company.address || company.phone || company.website || company.openingHours) && (
        <ul className="space-y-2 rounded-card border border-border bg-surface p-4 text-sm">
          {company.openingHours && (
            <li className="flex items-start gap-2.5">
              <Clock size={17} className="mt-0.5 shrink-0 text-brand-ink" aria-hidden />
              <span>{company.openingHours}</span>
            </li>
          )}
          {company.address && (
            <li className="flex items-start gap-2.5">
              <MapPin size={17} className="mt-0.5 shrink-0 text-brand-ink" aria-hidden />
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${company.name} ${company.address}`)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-2"
              >
                {company.address}
              </a>
            </li>
          )}
          {company.phone && (
            <li className="flex items-start gap-2.5">
              <Phone size={17} className="mt-0.5 shrink-0 text-brand-ink" aria-hidden />
              <a href={`tel:${company.phone.replace(/\s/g, '')}`} className="underline underline-offset-2">
                {company.phone}
              </a>
            </li>
          )}
          {company.website && (
            <li className="flex items-start gap-2.5">
              <Globe size={17} className="mt-0.5 shrink-0 text-brand-ink" aria-hidden />
              <a
                href={company.website}
                target="_blank"
                rel="noreferrer noopener"
                className="break-all underline underline-offset-2"
              >
                {company.website.replace(/^https?:\/\//, '')}
              </a>
            </li>
          )}
        </ul>
      )}

      <section>
        <h2 className="mb-3 font-display text-lg font-extrabold">
          {companyOffers.length === 1 ? 'Erbjudandet' : 'Erbjudanden'}
        </h2>
        {companyOffers.length === 0 ? (
          <EmptyState
            title="Inga erbjudanden i den här perioden"
            description={`${company.name} är inte med i ${booklet?.periodName ?? 'det aktuella häftet'}.`}
          />
        ) : (
          <ul className="space-y-2.5">
            {companyOffers.map((offer) => (
              <li key={offer.id}>
                <OfferCard
                  offer={offer}
                  remaining={remainingFor(offer)}
                  owned={owned}
                  onClick={() => setOpenOffer(offer)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {booklet && (
        <OfferDetail
          offer={openOffer}
          onClose={() => setOpenOffer(null)}
          remaining={openOffer ? remainingFor(openOffer) : 0}
          owned={owned}
          bookletStatus={booklet.status}
          bookletStartDate={booklet.startDate}
          bookletEndDate={booklet.endDate}
        />
      )}
    </div>
  )
}
